import { useCallback, useState } from 'react'
import { 
  format, 
  formatISO, 
  isSameMonth, 
  lastDayOfMonth, 
  startOfMonth, 
  subMonths 
} from 'date-fns'
import { ptBR } from 'date-fns/locale'

import metricsService from 'services/metrics'

import { 
  IFetchSchedulesAverageParams, 
  IMonthData, 
  ISchedulesAverage, 
  ISchedulesAverageData, 
  IUseSchedulesAverage 
} from '../types'

function sortDataDescending(data: ISchedulesAverageData[]) {
  return data.sort((a, b) => {
    const dateA = new Date(a.year)
    const dateB = new Date(b.year)

    return dateB.getTime() - dateA.getTime()
  }).reverse()
}

const formatDateFromSchedule = (data: IMonthData) => {
  const splitDate = data.month.split('-')
  const year = splitDate[0]
  const monthNumber = splitDate[1]
  const formattedDate = new Date(Number(year), Number(monthNumber) - 1, 1)
  const formattedYear = format(formattedDate, 'yy')
  const formattedMonth = format(formattedDate, 'MMM', {
    locale: ptBR
  })

  return {
    month: formattedMonth,
    year: formattedYear,
    date: formattedDate,
    average: data.average,
  }
}

const formatData = (data: ISchedulesAverage) => {
  const formattedData = data.monthly.map((scheduleData) => 
    formatDateFromSchedule(scheduleData)
  )
  
  return sortDataDescending(formattedData)
}

export const useSchedulesAverage = (): IUseSchedulesAverage => {
  const [data, setData] = useState<ISchedulesAverageData[]>([])
  const [loading, setLoading] = useState(false)

  const fetchSchedulesAverage = useCallback(async({
    selectedDate,
    planGroupFrequency
  }: IFetchSchedulesAverageParams) => {
    try {
      setLoading(true)
      const response = await metricsService.getSchedulesAverage ({
        startDate: formatISO(startOfMonth(new Date(selectedDate.startDate))),
        endDate: formatISO(lastDayOfMonth(new Date(selectedDate.endDate))),
        planGroupFrequency
      })

      const formattedData = formatData(response)

      setData(prevState => (
        [...prevState, ...formattedData]
      ))

    } catch(err) {
      setLoading(false)
    } finally {
      setLoading(false)
    }
  }, [])

  const getLastMonthData = () => {
    if(!data.length) return null

    const lastMonth = startOfMonth(subMonths(new Date(), 1))
    const lastMonthData = data.find(({ date }) => isSameMonth(lastMonth, date))
    const lastMonthAverage = lastMonthData?.average?.toFixed(1)

    return {
      average: lastMonthAverage?.replace('.', ','),
      month: lastMonthData?.month
    } 
  }

  return { 
    data,
    setScheduleData: setData,
    loading, 
    fetchSchedulesAverage, 
    getLastMonthData 
  }
}